import React, { Fragment, useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ProtectedRoute from '~/routes/ProtectedRoute';
import PermissionRoute from '~/routes/PermissionRoute';
import SecurityRoute from '~/routes/SecurityRoute';
import PublicRoute from '~/routes/PublicRoute';
import { AuthProvider } from '~/contexts/AuthContext';
import { UserAvatarProvider } from '~/contexts/UserAvatarContext';
import { AppParamsProvider } from '~/contexts/AppParamsContext';
import { PlanFeatureProvider } from '~/contexts/PlanFeatureContext';
import * as Pages from "~/pages";
import * as Layouts from "~/layouts";
import { UpgradePlanModalProvider } from '~/contexts/UpgradePlanModalContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWifi } from "@fortawesome/free-solid-svg-icons";
import JsonRoute from '~/assets/routes.json';
import SessionExpiredOverlay from '~/components/SessionExpiredOverlay';
import { FlashBarProvider } from '~/contexts/FlashBarContext';
import { FlashBarContainer } from '~/components/FlashBar';
import { LoaderProvider, useLoader } from '~/contexts/LoaderContext';

const SuspenseLoaderSignal = () => {
    const { showLoader, hideLoader } = useLoader();
    useEffect(() => {
        showLoader();
        return () => hideLoader();
    }, [showLoader, hideLoader]);
    return null;
};

const PageTransition = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -15 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ width: "100%", height: "100%" }}
    >
        {children}
    </motion.div>
);

const AppRoutesComponent = ({ appRoutes }) => {
    const location = useLocation();

    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                {appRoutes.map((route, index) => {
                    const Layout = Layouts[route.Layout] || Fragment;
                    const Page = Pages[route.Component];

                    if (!Page) return null;

                    if (route.IsPublic) {
                        if (route.Layout === 'BreakThroughLayout') {
                            return (
                                <Route
                                    key={index}
                                    path={route.Path}
                                    element={
                                        <PublicRoute>
                                            <AppParamsProvider>
                                                <Suspense fallback={<Fragment />}>
                                                    <Layout>
                                                        <PageTransition>
                                                            <Page />
                                                        </PageTransition>
                                                    </Layout>
                                                </Suspense>
                                            </AppParamsProvider>
                                        </PublicRoute>
                                    }
                                />
                            );
                        }
                        const publicContent = (
                            <Suspense fallback={<SuspenseLoaderSignal />}>
                                <Layout>
                                    <PageTransition>
                                        <Page />
                                    </PageTransition>
                                </Layout>
                            </Suspense>
                        );
                        return (
                            <Route
                                key={index}
                                path={route.Path}
                                element={
                                    route.Layout === 'AuthenticationLayout' ? publicContent : (
                                        <AppParamsProvider>
                                            {publicContent}
                                        </AppParamsProvider>
                                    )
                                }
                            />
                        );
                    } else {
                        const roles = route.Roles ? route.Roles : [];
                        if (route.Layout === 'AIChatbotLayout') {
                            return (
                                <Route
                                    key={index}
                                    path={route.Path}
                                    element={
                                        <ProtectedRoute requiredRoles={roles} area={route.Area}>
                                            <UserAvatarProvider>
                                                <PlanFeatureProvider>
                                                    <Suspense fallback={<SuspenseLoaderSignal />}>
                                                        <Layout>
                                                            <PermissionRoute requiredPermissions={route.Permissions} area={route.Area}>
                                                                <SecurityRoute>
                                                                    <PageTransition>
                                                                        <Page />
                                                                    </PageTransition>
                                                                </SecurityRoute>
                                                            </PermissionRoute>
                                                        </Layout>
                                                    </Suspense>
                                                </PlanFeatureProvider>
                                            </UserAvatarProvider>
                                        </ProtectedRoute>
                                    }
                                />
                            );
                        }
                        return (
                            <Route
                                key={index}
                                path={route.Path}
                                element={
                                    <ProtectedRoute requiredRoles={roles} area={route.Area}>
                                        <UserAvatarProvider>
                                            <Suspense fallback={<SuspenseLoaderSignal />}>
                                                <Layout>
                                                    <PermissionRoute requiredPermissions={route.Permissions} area={route.Area}>
                                                        <SecurityRoute>
                                                            <PageTransition>
                                                                <Page />
                                                            </PageTransition>
                                                        </SecurityRoute>
                                                    </PermissionRoute>
                                                </Layout>
                                            </Suspense>
                                        </UserAvatarProvider>
                                    </ProtectedRoute>
                                }
                            />
                        );
                    }
                })}
            </Routes>
        </AnimatePresence>
    );
};


function App() {
    const [appRoutes, setAppRoutes] = useState(JsonRoute);
    const [netWorkFailed, setNetWorkFailed] = useState(false);

    if (netWorkFailed) {
        return (
            <div style={{
                height: '100vh'
            }} className="d-flex justify-content-center align-items-center flex-column">
                <FontAwesomeIcon icon={faWifi} className="me-2 text-danger fs-6" />
                <div className="text-center">
                    <p className="fs-8-1 mt-2 mb-0 text-danger fw-bold">Network Error</p>
                    <p className="fs-9 mt-2 mb-0">Oops! We couldn’t connect to the server. Please try again later!</p>
                    <a className="fs-9"
                        href="#"
                        onClick={() => window.location.reload()}
                    >Try again</a>
                </div>
            </div>
        );
    }
    return (
        <Fragment>
            <AuthProvider>
                <FlashBarProvider>
                    <UpgradePlanModalProvider>
                        <LoaderProvider>
                            <Router>
                                <div className="app">
                                    <FlashBarContainer />
                                    <AppRoutesComponent appRoutes={appRoutes} />
                                </div>
                            </Router>
                        </LoaderProvider>
                    </UpgradePlanModalProvider>
                </FlashBarProvider>
            </AuthProvider>
            <SessionExpiredOverlay />
        </Fragment>
    );
}

export default App;
